/**
 * Avanzamento di una ricerca in corso (F-084).
 *
 * Compatto e in carattere piccolo, come chiesto: è un pannello di servizio, non
 * il contenuto della pagina — quello sono i risultati, che non ci sono ancora.
 *
 * ## Tre distinzioni che il pannello non appiattisce
 *
 * 1. **Un evento senza istante mostra «—», non un orario plausibile.** Le fasi
 *    concluse si deducono da ciò che il task ha persistito per poter riprendere
 *    dopo un errore: quel dato conserva l'esito, non quando è arrivato.
 *    Inventare l'orario del run sarebbe verosimile e falso.
 * 2. **Zero token e token ignoti non sono la stessa cosa.** Se nessuna chiamata
 *    ha riportato l'uso, si scrive «non riportati» invece di `0`, che direbbe
 *    «non ha consumato nulla».
 * 3. **Le chiamate senza `usage` si contano a parte.** È il buco dichiarato: il
 *    totale dei token è vero per le chiamate che l'hanno detto, e il pannello
 *    dice quante non l'hanno fatto invece di far sembrare il totale completo.
 */

import { formatDateTime, formatNumber } from '@/lib/format';
import type { RunProgress } from './api';
import './discovery.css';

/** `14:32:07` — l'ora basta: la cronologia è di minuti, non di giorni. */
function clockOf(at: string | null): string {
  if (!at) return '—';
  return formatDateTime(at).slice(-8);
}

export function RunProgressPanel({
  progress,
  current,
}: {
  progress: RunProgress;
  /** L'operazione dichiarata adesso dal task (`run.progress`). */
  current: string | null;
}) {
  const { llmCalls, totalTokens, callsWithoutUsage, events } = progress;
  // Le ultime in fondo, ma con le più recenti visibili senza scorrere tutto:
  // la lista è corta per costruzione (una ricerca fa una manciata di chiamate).
  const recent = events.slice(-12);

  return (
    <div className="run-progress">
      <div className="run-progress__head">
        <span className="run-progress__now">{current ?? 'In corso…'}</span>
        <span className="run-progress__counters numeric">
          {formatNumber(llmCalls)} chiamate ·{' '}
          {totalTokens === null ? 'token non riportati' : `${formatNumber(totalTokens)} token`}
          {callsWithoutUsage > 0 && ` · ${formatNumber(callsWithoutUsage)} senza conteggio`}
        </span>
      </div>

      {recent.length > 0 && (
        <ul className="run-progress__log">
          {recent.map((event, index) => (
            <li key={`${event.kind}-${event.at ?? 'na'}-${index}`} className={`run-progress__ev run-progress__ev--${event.kind}`}>
              <span className="run-progress__time numeric">{clockOf(event.at)}</span>
              <span className="run-progress__label">{event.label}</span>
              {event.detail && <span className="run-progress__detail">{event.detail}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
